import { BKJIcon } from '@awais4123/fillify-design-library';
import { JaneAvatar } from 'components/BKJJaneAvatar';
import { Flex } from 'components/Flex';
import { ShiftStatusBadge } from 'components/ShiftStatusBadge';
import { Tags } from 'components/Tags';
import { Fragment } from 'react';
import { useTranslation } from 'hooks/useTranslation';
import { formatJaneNameByStatus } from 'utils';
import { getTranslatedResponse } from 'utils/getTranslatedResponse';
import { withMemo } from 'utils/withMemo';
import { IShiftCardSkeletonProps } from 'components/ShiftCardSkeleton//ShiftCardSkeleton.types';

import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import {
  BKJStickyTableData,
  BKJTableRowWrapper,
  Label,
} from 'components/BKJTable/BKJTableRow/BKJTableRow.styled';
import {
  ShiftsTableData,
  Recurring,
  PremiumJaneName,
  Text,
  PositionName,
} from 'pages/ShiftsListPage/ShiftsTableRow/ShiftsTableRow.styled';
import {
  formatDateForShiftTableRow,
  getHoursAndMinutesFromDate,
  calculateBreakTime,
  calculateDuration,
} from 'pages/ShiftsListPage/ShiftsTableRow/ShiftsTableRow.utils';
import { RenderShiftActions } from 'components/RenderShiftActions';
import { UserGuard } from 'guards/UserGuard';
import { UserPermissionEnum } from 'guards/UserGuard.types';
import { usePathname } from 'hooks/usePathname';
import { DashboardPageRouteType } from 'pages/DashboardPage/DashboardPage.types';
import { AllowOvertimeDialog } from 'components/AllowOvertimeDialog';
import { ContinueCalloutDialog } from 'components/ContinueCalloutDialog';
import { AutoRebidDialog } from 'components/AutoRebidDialog';
import { toastService } from 'services';
import { queryRefetchService } from 'services/QueryRefetchService';

export const WebShiftCardRow = withMemo(function (
  props: IShiftCardSkeletonProps,
): JSX.Element | null {
  const { t, i18n } = useTranslation();
  const {
    id,
    bidders,
    biddable,
    biddingTimeEndsAt,
    status,
    mainJane,
    dedicated,
    sentToAgencies,
    acceptedBySharedTeamMember,
    position,
    startAt,
    endAt,
    recurrence,
    orderSchedule,
    totalDuration,
    currentlyActiveCalloutList,
  } = props.order;

  const {
    shiftActions,
    isSharedJanePrimaryCommunityDifferentFromSelectedLocation,
    handleCardClick,
    ShiftActionComponent,
    callOutStatus,
    isContinueCallOutConfirmationOpen,
    setIsContinueCallOutConfirmationOpen,
    isAllowOvertimeConfirmationOpen,
    setIsAllowOvertimeConfirmationOpen,
    isAutoRebidConfirmationOpen,
    setIsAutoRebidConfirmationOpen,
  } = props;

  const { timeZone } = getCurrentlySelectedLocation();
  const activeTab = usePathname() as DashboardPageRouteType;

  const isPastShiftsPage = activeTab === '/dashboard/past';
  return (
    <Fragment>
      <AllowOvertimeDialog
        orderId={id}
        isAllowOvertimeConfirmationOpen={isAllowOvertimeConfirmationOpen}
        setIsAllowOvertimeConfirmationOpen={setIsAllowOvertimeConfirmationOpen}
        onSuccess={() => {
          toastService.success(t('dashboard:ALLOW_OVERTIME_SUCCESS'));
          queryRefetchService.refetchDataQueryByKey('SHIFT_CARD', `${id}`);
        }}
      />
      {callOutStatus === 'PAUSED' && currentlyActiveCalloutList?.id && (
        <ContinueCalloutDialog
          orderId={id}
          isContinueCallOutConfirmationOpen={isContinueCallOutConfirmationOpen}
          setIsContinueCallOutConfirmationOpen={setIsContinueCallOutConfirmationOpen}
          onSuccess={() => {
            toastService.success(t('dashboard:CALL_OUT_SUCCESSFUL_RESUMED'));
            queryRefetchService.refetchDataQueryByKey('SHIFT_CARD', `${id}`);
          }}
        />
      )}
      <AutoRebidDialog
        orderId={id}
        isAutoRebidConfirmationOpen={isAutoRebidConfirmationOpen}
        setIsAutoRebidConfirmationOpen={setIsAutoRebidConfirmationOpen}
        onSuccess={() => {
          toastService.success(t('success:SHIFT_BIDDING_SUCCESSFULLY_AUTO_EXTENDED'));
          queryRefetchService.refetchDataQueryByKey('SHIFT_CARD', `${id}`);
        }}
      />
      <BKJTableRowWrapper onClick={handleCardClick} data-cy={`ShiftCard-${status.value}`}>
        <ShiftsTableData>
          <Label>Shift Date</Label>
          <Flex flexDirection="column">
            <Text>{formatDateForShiftTableRow(startAt, timeZone, true, false)}</Text>
            {recurrence && (
              <Recurring>
                <BKJIcon iconName="Recurring" />
                &nbsp;Recurring Shift
              </Recurring>
            )}
          </Flex>
        </ShiftsTableData>
        <ShiftsTableData>
          <Label>Team Member</Label>
          <Flex alignItems="center" justifyContent="space-between" width="100%">
            <Flex alignItems="center">
              <Flex margin="0 13px 0 0">
                <JaneAvatar
                  size={30}
                  status={status.value}
                  url={mainJane?.avatarUrls.small}
                  pendingSignup={mainJane?.pendingSignup ?? false}
                />
              </Flex>
              <PremiumJaneName>
                {formatJaneNameByStatus({ status: status.name, mainJane })}
                {isSharedJanePrimaryCommunityDifferentFromSelectedLocation && (
                  <Fragment>
                    &nbsp; &nbsp;
                    <BKJIcon iconName="Group2" width={16} height={14} />
                  </Fragment>
                )}
              </PremiumJaneName>
            </Flex>
            &nbsp; &nbsp;
            <Flex alignItems="center" justifyContent="center">
              <Tags
                id={id}
                status={status.value}
                isRecurring={false}
                isDedicated={dedicated}
                isSentToAgencies={sentToAgencies}
                hasJane={!!mainJane}
                janePrimaryCommunity={mainJane?.primaryCommunity}
                isAgencyJane={!!mainJane?.agencyCommunity}
                isAcceptedBySharedTeamMember={!!acceptedBySharedTeamMember}
                biddable={biddable}
                bidders={bidders}
                biddingTimeEndsAt={biddingTimeEndsAt}
              />
            </Flex>
          </Flex>
        </ShiftsTableData>
        <ShiftsTableData>
          <Label>Position</Label>
          <PositionName>{position.name}</PositionName>
        </ShiftsTableData>
        <ShiftsTableData>
          <Label>Status</Label>
          <ShiftStatusBadge status={status.value}>
            {getTranslatedResponse(status?.name, 'status', i18n, t)}
          </ShiftStatusBadge>
        </ShiftsTableData>
        <ShiftsTableData>
          <Label>Start Time</Label>
          <Text>{getHoursAndMinutesFromDate(startAt, timeZone)}</Text>
        </ShiftsTableData>
        <ShiftsTableData>
          <Label>End Time</Label>
          <Text>{getHoursAndMinutesFromDate(endAt, timeZone)}</Text>
        </ShiftsTableData>
        <ShiftsTableData>
          <Label>Unpaid Break Time</Label>
          <Text>{calculateBreakTime(orderSchedule)}</Text>
        </ShiftsTableData>
        <ShiftsTableData>
          <Label>Total Duration</Label>
          <Text>{calculateDuration(totalDuration ?? null)}</Text>
        </ShiftsTableData>
        <ShiftsTableData>
          <Label>Shift No. </Label>
          <Text>#{orderSchedule.id}</Text>
        </ShiftsTableData>
        {!isPastShiftsPage && (
          <BKJStickyTableData css="background: #fff; display: flex; justify-content: center">
            <Label>Action </Label>
            <UserGuard
              userType={[
                UserPermissionEnum.USER_TYPE_B2B,
                UserPermissionEnum.USER_TYPE_PREMIUM_PRIMARY,
                UserPermissionEnum.USER_TYPE_PREMIUM_SECONDARY,
              ]}
            >
              {!!shiftActions.length ? (
                <RenderShiftActions
                  variant="PurpleSolid"
                  shiftActions={shiftActions}
                  SingleItemComponent={ShiftActionComponent}
                />
              ) : (
                '-'
              )}
            </UserGuard>
          </BKJStickyTableData>
        )}
      </BKJTableRowWrapper>
    </Fragment>
  );
});
